import { SearchOutlined, DoubleRightOutlined, ClearOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { Flex, Spin, Input, Typography, Table, Layout, Space, Button, Tooltip, App, Modal, Tag, Empty } from 'antd'
import { useCallback, useMemo, useState, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useCustomerProfileStore } from '@/stores/customer-profile'
import useAuthStore from '@/stores/AuthStore'
import { isEmpty } from '@haina/utils-commons'
import { Conditional } from '@/components/Conditional'
import { LineItem } from './components/line-item'

const EMPTY_LOCALE = {
  emptyText: <Empty description='搜索出你要合并的客人' image={Empty.PRESENTED_IMAGE_SIMPLE} />,
}

const SOURCE_COLUMNS = [
  {
    title: '名字/邮箱',
    dataIndex: 'customer_name',
    width: '180px',
    render: (_, customer) => (
      <Space direction='vertical' size={0}>
        <Typography.Text>{customer.customer_name}</Typography.Text>
        <Typography.Text type='secondary' style={{ fontSize: '12px' }}>
          {customer.customer_email || '-'}
        </Typography.Text>
      </Space>
    ),
  },
  {
    title: '订单号',
    dataIndex: 'order_id',
    width: '120px',
    render: (text) => text || '-',
  },
]

/**
 * Merge Customer Profiles Interface
 * Left: Source guests (待合并客人) - searchable, multi-select list
 * Right: Target guest (合并到的客人) - auto-wired to the current customerDetail from the main view
 */
function MergeCustomerProfile() {
  const [spinning, setSpinning] = useState(false)
  const [sourceList, setSourceList] = useState([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  const sourceKeywordRef = useRef(null)

  const { notification } = App.useApp()

  const loginUser = useAuthStore((s) => s.loginUser)
  const [fetchCustomerList, customerDetail, mergeCustomerProfile] = useCustomerProfileStore(useShallow((s) => [s.fetchCustomerList, s.customerDetail, s.mergeCustomerProfile]))

  const selectedSources = useMemo(() => sourceList.filter((s) => selectedRowKeys.includes(s.global_customer_id)), [sourceList, selectedRowKeys])

  const selectedSourceIds = useMemo(() => new Set(selectedRowKeys), [selectedRowKeys])

  const searchSourceList = useCallback(() => {
    const keyword = sourceKeywordRef.current?.input?.value
    if (isEmpty(keyword)) {
      notification.warning({ message: '请输入搜索关键词', description: '名字、邮箱或订单号', placement: 'topLeft' })
      return
    }
    setSpinning(true)
    fetchCustomerList(keyword)
      .then((result) => {
        const currentId = useCustomerProfileStore.getState().customerDetail?.global_customer_id
        const filtered = (result || []).filter((item) => item.global_customer_id !== currentId)
        setSourceList(filtered.slice(0, 10))
      })
      .catch((err) => {
        notification.error({ message: '网络错误', description: err.message, placement: 'topLeft' })
      })
      .finally(() => setSpinning(false))
  }, [fetchCustomerList, notification])

  const clearSources = useCallback(() => {
    setSelectedRowKeys([])
    setSourceList([])
    if (sourceKeywordRef.current?.input) sourceKeywordRef.current.input.value = ''
  }, [])

  const targetGuestName = useMemo(() => {
    if (customerDetail?.first_name) {
      return `${customerDetail.first_name} ${customerDetail.last_name ?? ''}`.trim()
    }
    return customerDetail?.global_customer_id ? `ID: ${customerDetail.global_customer_id}` : ''
  }, [customerDetail])

  const executeMerge = useCallback(async () => {
    if (selectedSources.length === 0 || !customerDetail) {
      notification.warning({ message: '请先选择源客人', placement: 'topLeft' })
      return
    }
    if (confirmText !== targetGuestName) {
      notification.warning({ message: '请输入正确的目标客人名字以确认合并', placement: 'topLeft' })
      return
    }
    const targetId = customerDetail.global_customer_id
    if (selectedSources.some((s) => s.global_customer_id === targetId)) {
      notification.error({ message: '不能合并到同一个客人', description: '源客人和目标客人不能相同', placement: 'topLeft' })
      return
    }

    setSpinning(true)
    mergeCustomerProfile(customerDetail.global_customer_id, selectedRowKeys, `${loginUser.username}(${loginUser.userId})`)
      .then(() => {
        notification.success({
          message: '批量合并完成',
          description: `成功合并客人到 ${customerDetail.first_name || customerDetail.global_customer_id}`,
        })
      })
      .catch((err) => {
        notification.error({
          message: '出错了',
          description: err,
        })
      })
      .finally(() => {
        setSelectedRowKeys([])
        setSourceList([])
        setConfirmText('')
        setConfirmModalOpen(false)
        setSpinning(false)
      })
  }, [selectedSources, customerDetail, notification, confirmText, targetGuestName, mergeCustomerProfile, loginUser, selectedRowKeys])

  const handleCancelModal = useCallback(() => {
    setConfirmModalOpen(false)
    setConfirmText('')
  }, [])

  const okButtonProps = useMemo(
    () => ({
      danger: true,
      disabled: confirmText !== targetGuestName,
    }),
    [confirmText, targetGuestName],
  )

  const handleTableRow = useCallback(
    (customer) => ({
      className: selectedSourceIds.has(customer.global_customer_id) ? 'bg-ant-row-selection cursor-pointer' : 'cursor-pointer hover:bg-ant-row-hover',
    }),
    [selectedSourceIds],
  )

  return (
    <>
      <Spin spinning={spinning} fullscreen />

      <Modal
        title={
          <>
            <ExclamationCircleOutlined className='text-orange-500 mr-2' />
            确认合并
          </>
        }
        open={confirmModalOpen}
        onOk={executeMerge}
        onCancel={handleCancelModal}
        okText='确认'
        cancelText='取消'
        okButtonProps={okButtonProps}>
        <div className='py-4'>
          <Typography.Paragraph>
            <Typography.Text strong>
              你将合并这 <span className='text-blue-600'>{selectedSources.length + 1} 个</span> 客人：
            </Typography.Text>
          </Typography.Paragraph>
          <div className='bg-gray-50 p-4 rounded-lg my-3'>
            <div className='flex flex-wrap gap-1'>
              <Tag color='green'>{targetGuestName}</Tag>
              {selectedSources.map((s) => (
                <Tag key={s.global_customer_id} color='blue'>
                  {s.customer_name}
                </Tag>
              ))}
            </div>
          </div>
          <div className='mt-4 p-3 bg-red-50 border border-red-200 rounded-lg'>
            <Typography.Text strong className='text-red-600'>
              合并后不可撤销，请谨慎操作。
            </Typography.Text>
            <Input className='mt-2' placeholder={`请输入 "${targetGuestName}"`} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
          </div>
        </div>
      </Modal>

      <Layout>
        <Layout.Sider width='420' theme='light' className='relative border-r border-gray-200'>
          <Typography.Title level={4}>
            <Tag color='blue' className='mr-2'>
              待合并
            </Tag>
            客人
            {selectedSources.length > 0 && <span className='text-sm font-normal text-blue-600 ml-1'>(已选 {selectedSources.length})</span>}
          </Typography.Title>

          <Space.Compact style={{ width: '100%' }} className='mb-3'>
            <Input allowClear placeholder='搜索名字、邮箱、订单号' ref={sourceKeywordRef} onPressEnter={searchSourceList} />
            <Tooltip title='搜索'>
              <Button type='primary' icon={<SearchOutlined />} onClick={searchSourceList} />
            </Tooltip>
          </Space.Compact>

          <Table
            rowKey='global_customer_id'
            pagination={false}
            columns={SOURCE_COLUMNS}
            dataSource={sourceList}
            locale={EMPTY_LOCALE}
            rowSelection={{
              hideSelectAll: true,
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            onRow={handleTableRow}
          />
        </Layout.Sider>

        <Layout.Content style={{ minWidth: '80px', maxWidth: '80px' }} className='flex items-center justify-center bg-gray-50'>
          <Flex vertical gap='middle' align='center'>
            <Tooltip title='执行合并'>
              <Button
                type='primary'
                shape='circle'
                icon={<DoubleRightOutlined />}
                size='large'
                danger
                onClick={() => {
                  if (selectedSources.length === 0 || !customerDetail) {
                    notification.warning({ message: '请先选择源客人', placement: 'topLeft' })
                    return
                  }
                  setConfirmText('')
                  setConfirmModalOpen(true)
                }}
                disabled={selectedSources.length === 0 || !customerDetail}
              />
            </Tooltip>
          </Flex>
        </Layout.Content>

        <Layout.Sider width='420' theme='light' className='relative border-l border-gray-200'>
          <Typography.Title level={4} className='mt-4'>
            <Tag color='green' className='mr-2'>
              合并到
            </Tag>
            客人
          </Typography.Title>

          <div className='bg-white p-1 max-w-4xl mx-auto pb-4 border-solid border-0 border-b border-gray-200'>
            <LineItem title='名字' value={customerDetail?.first_name ? `${customerDetail.first_name} ${customerDetail.last_name ?? ''}` : '-'} />
            <LineItem title='性别' value={customerDetail?.gender} />
            <LineItem title='国籍' value={customerDetail?.country} />
            <LineItem title='ID' value={customerDetail?.global_customer_id} />
          </div>

          <Conditional
            condition={selectedSources.length > 0}
            whenTrue={
              <div className='mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg'>
                <Flex justify='space-between' align='center' className='mb-2'>
                  <Typography.Text strong className='text-blue-700'>
                    已选 {selectedSources.length} 个源客人
                  </Typography.Text>
                  <Button type='text' size='small' icon={<ClearOutlined />} onClick={clearSources} danger>
                    清除全部
                  </Button>
                </Flex>
                <div className='flex flex-wrap gap-1'>
                  {selectedSources.map((s) => (
                    <Tag
                      key={s.global_customer_id}
                      closable
                      color='blue'
                      onClose={(e) => {
                        e.preventDefault()
                        setSelectedRowKeys((prev) => prev.filter((id) => id !== s.global_customer_id))
                      }}>
                      {s.customer_name}
                    </Tag>
                  ))}
                </div>
              </div>
            }
          />
        </Layout.Sider>
      </Layout>
    </>
  )
}

export default MergeCustomerProfile
